import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// =============================================================================
// RouteTypeToggle — seletor do tipo de rota (retorno opcional) — handoff 0057.
//
// Controle compartilhado pelos dois níveis de escolha do route_type:
//   - nível 1: default da otimização (FleetOptimizeModal);
//   - nível 2: override por rota (SubclusterFormModal).
//
// Valores fixos do contrato (literais em inglês, não traduzir o value):
//   'round_trip' → vai e volta (termina na base)
//   'one_way'    → só vai (termina na última parada)
//
// É um SELETOR, não uma ação — não escreve no banco por si só (o "Otimizar"/
// "Salvar" do modal é que grava). Por isso é radio group semântico/acessível,
// sem cor de ação (FC7): dois <input type="radio"> reais com label associável.
// =============================================================================

const Fieldset = styled.fieldset`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin: 0;
   padding: 0;
   border: 0;

   legend {
      padding: 0;
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }
`;

const Option = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 8px 10px;
   border-radius: 8px;
   border: 1px solid
      ${({ theme, $active }) =>
         $active
            ? theme?.colors?.actionWrite ?? '#4098ef'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $active }) =>
      $active ? 'rgba(64, 152, 239, 0.08)' : 'transparent'};
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
   transition: border-color 0.12s ease, background 0.12s ease;

   input {
      margin: 2px 0 0;
      accent-color: ${({ theme }) => theme?.colors?.actionWrite ?? '#4098ef'};
   }
`;

const OptionText = styled.span`
   display: flex;
   flex-direction: column;
   gap: 2px;

   strong {
      font-size: 0.82rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   }

   small {
      font-size: 0.72rem;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#666'};
      line-height: 1.4;
   }
`;

const OPTIONS = [
   {
      value: 'round_trip',
      title: 'Retorna à base',
      hint: 'A rota vai até as paradas e volta ao ponto de origem.',
   },
   {
      value: 'one_way',
      title: 'Não retorna',
      hint: 'O técnico encerra na última parada (sem a perna de volta).',
   },
];

export default function RouteTypeToggle({
   value,
   onChange,
   disabled = false,
   name = 'route-type',
   legend = 'Retorno da rota',
}) {
   return (
      <Fieldset disabled={disabled}>
         <legend>{legend}</legend>
         {OPTIONS.map((opt) => (
            <Option
               key={opt.value}
               $active={value === opt.value}
               $disabled={disabled}
            >
               <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                  disabled={disabled}
               />
               <OptionText>
                  <strong>{opt.title}</strong>
                  <small>{opt.hint}</small>
               </OptionText>
            </Option>
         ))}
      </Fieldset>
   );
}

RouteTypeToggle.propTypes = {
   // 'round_trip' | 'one_way'
   value: PropTypes.oneOf(['round_trip', 'one_way']).isRequired,
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
   // name do grupo de radios (distinto por modal se coexistirem no DOM).
   name: PropTypes.string,
   legend: PropTypes.string,
};
